import React, { useRef, useState, useEffect } from 'react';
import {
  Camera,
  Image as ImageIcon,
  Trash2,
  CheckCircle2,
  RefreshCw,
  X,
  FlipHorizontal,
} from 'lucide-react';
import { Modal, Button } from '../common';

interface PhotoCaptureProps {
  itemId: string;
  photoUrl?: string;
  onChange: (dataUrl?: string) => void;
  disabled?: boolean;
}

/**
 * Compresses an image file using an HTML5 canvas down to max 1280px dimension
 * and JPEG quality ~0.75, ensuring file size is strictly under 300KB.
 */
async function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX_WIDTH = 1280;
        const MAX_HEIGHT = 1280;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas 2D context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Compress to JPEG 0.72 quality
        let quality = 0.72;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);

        // Check size: 300KB in base64 is approximately 400,000 characters
        if (dataUrl.length > 390000) {
          quality = 0.55;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

export const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  itemId,
  photoUrl,
  onChange,
  disabled = false,
}) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isCompressing, setIsCompressing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isLiveCameraOpen, setIsLiveCameraOpen] = useState(false);
  const [cameraFacing, setCameraFacing] = useState<'environment' | 'user'>('environment');
  const [cameraLoading, setCameraLoading] = useState(false);

  // Stop camera stream safely
  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  // Clean up on unmount or close
  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, []);

  // Start live camera stream
  const startLiveCamera = async (facing: 'environment' | 'user' = 'environment') => {
    setErrorMsg('');
    setCameraLoading(true);
    stopCameraStream();

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('WebRTC not supported');
      }

      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsLiveCameraOpen(true);
    } catch (err: any) {
      console.warn('[PhotoCapture] getUserMedia failed, falling back to input capture:', err);
      stopCameraStream();
      setIsLiveCameraOpen(false);
      // Fallback: trigger native file input with camera intent
      cameraInputRef.current?.click();
    } finally {
      setCameraLoading(false);
    }
  };

  const handleOpenLiveCamera = () => {
    startLiveCamera(cameraFacing);
  };

  const handleCloseLiveCamera = () => {
    stopCameraStream();
    setIsLiveCameraOpen(false);
  };

  const handleFlipCamera = () => {
    const nextFacing = cameraFacing === 'environment' ? 'user' : 'environment';
    setCameraFacing(nextFacing);
    startLiveCamera(nextFacing);
  };

  // Capture snapshot from live video stream
  const handleSnapPhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) return;

    try {
      setIsCompressing(true);
      const canvas = document.createElement('canvas');
      const MAX_DIM = 1280;
      let w = video.videoWidth;
      let h = video.videoHeight;

      if (w > h) {
        if (w > MAX_DIM) {
          h = Math.round((h * MAX_DIM) / w);
          w = MAX_DIM;
        }
      } else {
        if (h > MAX_DIM) {
          w = Math.round((w * MAX_DIM) / h);
          h = MAX_DIM;
        }
      }

      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D failed');

      ctx.drawImage(video, 0, 0, w, h);

      let quality = 0.72;
      let dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length > 390000) {
        quality = 0.55;
        dataUrl = canvas.toDataURL('image/jpeg', quality);
      }

      onChange(dataUrl);
      handleCloseLiveCamera();
    } catch (err) {
      console.error('[PhotoCapture] Snap photo error:', err);
      setErrorMsg('เกิดข้อผิดพลาดในการบันทึกภาพ');
    } finally {
      setIsCompressing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressing(true);
      setErrorMsg('');
      const compressedDataUrl = await compressImage(file);
      onChange(compressedDataUrl);
    } catch (err) {
      console.error('[PhotoCapture] Compression error:', err);
      setErrorMsg('เกิดข้อผิดพลาดในการบีบอัดรูปภาพ');
    } finally {
      setIsCompressing(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange(undefined);
    setErrorMsg('');
  };

  return (
    <div className="space-y-2">
      {/* 1. Direct Camera Input (Fallback) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled || isCompressing}
        onChange={handleFileChange}
        className="hidden"
        id={`camera-input-${itemId}`}
      />

      {/* 2. Gallery Input (Accepts image file extensions for system file picker) */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,.jpg,.jpeg,.png,.webp"
        disabled={disabled || isCompressing}
        onChange={handleFileChange}
        className="hidden"
        id={`gallery-input-${itemId}`}
      />

      {photoUrl ? (
        <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 p-2 flex items-center gap-3">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-slate-200 shrink-0 border border-slate-300">
            <img
              src={photoUrl}
              alt="Inspection photo evidence"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 mb-0.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              แนบรูปภาพแล้ว (&lt;300KB)
            </div>
            <p className="text-[11px] text-slate-500 truncate">
              บันทึกหลักฐานความผิดปกติลงในเครื่อง
            </p>
          </div>

          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="p-2.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center shrink-0 shadow-2xs cursor-pointer"
              title="ลบรูปภาพ"
              aria-label="Remove photo"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      ) : (
        <div>
          {isCompressing ? (
            <div className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50 text-blue-700 border border-blue-200 text-sm font-semibold min-h-[48px]">
              <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              กำลังประมวลผลและบีบอัดรูปภาพ (&lt;300KB)...
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Button 1: Live In-App Camera */}
              <button
                type="button"
                disabled={disabled || cameraLoading}
                onClick={handleOpenLiveCamera}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-xs sm:text-sm transition-all min-h-[44px] select-none active:scale-[0.98] cursor-pointer shadow-xs"
              >
                <Camera className="w-4 h-4 shrink-0" />
                <span>{cameraLoading ? 'กำลังเปิดกล้อง...' : 'เปิดกล้องถ่ายรูป (Camera)'}</span>
              </button>

              {/* Button 2: Choose from Gallery / Storage */}
              <button
                type="button"
                disabled={disabled}
                onClick={() => galleryInputRef.current?.click()}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 border border-slate-300 font-semibold text-xs sm:text-sm transition-all min-h-[44px] select-none active:scale-[0.98] cursor-pointer shadow-2xs"
              >
                <ImageIcon className="w-4 h-4 text-slate-600 shrink-0" />
                <span>เลือกจากคลังภาพ/เครื่อง (Gallery)</span>
              </button>
            </div>
          )}
          {errorMsg && <p className="text-xs text-rose-600 mt-1.5 font-semibold">{errorMsg}</p>}
        </div>
      )}

      {/* Live In-App Camera Modal Viewfinder */}
      {isLiveCameraOpen && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col justify-between p-4 animate-in fade-in duration-150 select-none">
          {/* Top Bar */}
          <div className="flex items-center justify-between z-10">
            <div className="text-white text-sm font-semibold flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-xs">
              <Camera className="w-4 h-4 text-blue-400 animate-pulse" />
              <span>กล้องถ่ายภาพความผิดปกติ</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleFlipCamera}
                className="p-2.5 rounded-full bg-white/20 text-white hover:bg-white/30 active:scale-95 transition-all"
                title="สลับกล้องหน้า/หลัง"
              >
                <FlipHorizontal className="w-5 h-5" />
              </button>

              <button
                type="button"
                onClick={handleCloseLiveCamera}
                className="p-2.5 rounded-full bg-white/20 text-white hover:bg-white/30 active:scale-95 transition-all"
                title="ปิดกล้อง"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Viewfinder Video Stream */}
          <div className="relative flex-1 flex items-center justify-center overflow-hidden my-4 rounded-2xl bg-black">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full max-h-[75vh] object-cover rounded-2xl border border-white/10"
            />

            {/* Target Reticle Overlay */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-64 h-64 border-2 border-white/40 rounded-xl flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-blue-400 rounded-full" />
              </div>
            </div>
          </div>

          {/* Bottom Action Controls */}
          <div className="flex items-center justify-around py-4 z-10">
            <button
              type="button"
              onClick={handleCloseLiveCamera}
              className="text-white/80 hover:text-white text-xs font-semibold px-4 py-2"
            >
              ยกเลิก
            </button>

            {/* Shutter Button */}
            <button
              type="button"
              onClick={handleSnapPhoto}
              className="w-18 h-18 rounded-full border-4 border-white flex items-center justify-center active:scale-90 transition-transform bg-blue-600 hover:bg-blue-500 shadow-lg cursor-pointer"
              title="กดเพื่อถ่ายรูป"
            >
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center">
                <Camera className="w-7 h-7 text-blue-600" />
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                handleCloseLiveCamera();
                galleryInputRef.current?.click();
              }}
              className="text-white/80 hover:text-white text-xs font-semibold px-4 py-2 flex items-center gap-1"
            >
              <ImageIcon className="w-4 h-4" />
              คลังภาพ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
